/* OfflineStatusPill — subtle mobile indicator. Toont:
 * - "Offline" wanneer navigator.onLine === false
 * - "N wachtend" wanneer er items in de offline-queue zitten
 * Klik = handmatige flush.
 */
import { useEffect, useState } from "react";
import { CloudOff, RefreshCw } from "lucide-react";
import { pendingCount, flushQueue } from "@/lib/offlineQueue";
import { toast } from "sonner";

export default function OfflineStatusPill() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onOn = () => setOnline(true);
    const onOff = () => setOnline(false);
    const refresh = async () => setPending(await pendingCount());
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    window.addEventListener("offline-queue-changed", refresh);
    window.addEventListener("offline-queue-poison", () => {
      toast.error("Een offline actie is geweigerd door de server");
      refresh();
    });
    refresh();
    return () => {
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
      window.removeEventListener("offline-queue-changed", refresh);
    };
  }, []);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    const r = await flushQueue();
    setBusy(false);
    if (r.sent > 0) toast.success(`${r.sent} offline actie(s) gesynchroniseerd`);
    else if (r.remaining > 0) toast.info(`${r.remaining} in de wachtrij — probeer later opnieuw`);
    setPending(await pendingCount());
  };

  if (online && pending === 0) return null;

  return (
    <button
      onClick={onClick}
      type="button"
      data-testid="offline-status-pill"
      className={`fixed lg:top-4 lg:right-4 bottom-20 right-3 z-40 flex items-center gap-2 px-3 py-1.5 rounded-full shadow-md text-xs font-medium ${
        !online ? "bg-red-600 text-white" : "bg-amber-500 text-white"
      }`}
      title={!online ? "Je bent offline — wijzigingen worden bewaard" : "Synchroniseer nu"}
    >
      {!online ? <CloudOff className="h-3.5 w-3.5" /> : <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />}
      <span>{!online ? (pending > 0 ? `Offline · ${pending}` : "Offline") : `${pending} wachtend`}</span>
    </button>
  );
}
